import { Form, Formik } from 'formik';
import {
  Button,
  Input,
  LoadingSpinner,
  Modal,
} from '@rabbit/elements/shared-components';
import * as Yup from 'yup';
import { useContext, useState } from 'react';
import { CaseflowContext } from 'apps/sage/src/context/CaseflowContext';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { AppContext } from '@rabbit/app-context';
import {
  CLAIM_STATUS_OPTIONS_FATBIKES,
  CLAIM_STATUS_OPTIONS_LITE,
} from '@rabbit/bizproc/react';
import { CaseFlowCaseStations, CaseflowCaseTypes } from '@rabbit/bizproc/core';
import { ClaimCaseEmailNotification } from '@rabbit/sage/utils/ClaimEmailNotification';
import { CaseFlowFacts } from '@rabbit/data/types';
import {
  PART_STATUS_OPTIONS,
  WARRANTY_STATUS_OPTIONS,
} from '@rabbit/sage/utils/consts';
import { updateHoldingOnClose } from '@rabbit/sage/utils/firestore-helpers';

interface FormValuesShape {
  status: string;
  warranty_status: string;
  comment_to_customer?: string;
  part_status?: string;
}

const validationSchema = Yup.object().shape({
  status: Yup.string().trim().required('Please select a status'),
  warranty_status: Yup.string(),
  part_status: Yup.string(),
  comment_to_customer: Yup.string()
    .test('remove-html-tags', 'please insert a message.', (value) => {
      const div = document.createElement('div');
      div.innerHTML = value || '';
      return !value || div.textContent?.trim() !== '';
    })
    .trim(),
});

export default function ClaimStatusModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { config, tenantInfo } = useContext(AppContext);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    caseFacts,
    alterCaseFacts,
    executeAction,
    moveSpotlight,
    operatingPersonaSingleKey,
    caseId,
    caseFlowCase,
    alterCasePublicEmail,
    caseActors,
  } = useContext(CaseflowContext) || {};

  if (
    !executeAction ||
    !alterCaseFacts ||
    !moveSpotlight ||
    !operatingPersonaSingleKey ||
    !caseFacts ||
    !caseId ||
    !caseFlowCase ||
    !alterCasePublicEmail ||
    !caseActors
  )
    return <LoadingSpinner size={'xs'} />;

  const {
    holding_faults,
    consumer_issue_description,
    consumer_issue_type,
    consumer_issue_type_ref,
    preliminary_assessment,
  } = caseFacts;
  let { final_assessment } = caseFacts;

  const initialValues = {
    status: caseFlowCase?.GetCaseState() || '',
    warranty_status: caseFacts?.warranty_status || '',
    part_status: caseFacts?.part_status || '',
  };

  const onSubmit = async (values: FormValuesShape) => {
    const { status, comment_to_customer, warranty_status, part_status } =
      values;
    if (warranty_status === 'Out of warranty') {
      final_assessment = 'Not covered';
    }
    let factUpdates: CaseFlowFacts = {
      holding_faults,
      consumer_issue_description,
      consumer_issue_type,
      consumer_issue_type_ref,
      preliminary_assessment,
      final_assessment,
      warranty_status,
      part_status,
    };

    setIsSubmitting(true);
    // update case actions
    try {
      if (status && status !== caseFlowCase.GetCaseState()) {
        // If the status is moved to preliminary assessment, we need to reset the repairer and the invoices
        if (
          status === CaseFlowCaseStations.PRELIMINARY_ASSESSMENT &&
          config.CLAIMS.CASEFLOW_TYPE === CaseflowCaseTypes.FATBIKES
        ) {
          executeAction &&
            (await executeAction('assign_case', {
              delegate_repairer_id: 'Unassigned',
              delegate_repairer_name: 'Unassigned',
            }));
          factUpdates = {
            ...factUpdates,
            external_repair_invoices: [],
            comment_to_customer: comment_to_customer,
          };
        }
        
        executeAction && (await executeAction(`move_to_${status}`));
        
        // Automatically move to archive if the status is closed
        if (status === CaseFlowCaseStations.CLOSED) {
          executeAction && (await executeAction('move_to_archived'));
          // Only a close detaches the case from the holding. Running this on
          // every transition emptied active_cases mid-claim, which flips
          // Olive's product page back to "Initiate claim" on a live claim.
          await updateHoldingOnClose(caseId, caseFacts?.consumer_holding ?? '');
        }
      }

      alterCaseFacts && (await alterCaseFacts(factUpdates));
      await ClaimCaseEmailNotification({
        status,
        caseFlowCase,
        tenantInfo,
        alterCasePublicEmail,
        t,
        comment_to_customer,
        blockCustomerEmails:
          config?.CLAIMS?.CONSUMER?.BLOCK_CUSTOMER_EMAIL_NOTIFICATIONS === true,
      });
      toast.success(t('Claim updated successfully.'));
      onClose();
    } catch (err) {
      setIsSubmitting(false);
      toast.error(t('message.failedToUpdateClaimDetails'));
      console.log(err);
    }
  };

  return (
    <Modal
      settings={{
        title: t('general.claimDetails'),
        headerBackground: true,
        handleClose: () => onClose(),
      }}
      kind="generic"
      isLoading={isSubmitting}
      className="max-w-[1024px]"
    >
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        {({ errors }) => (
          <Form className="mt-5 flex flex-col gap-3 px-4">
            <Input
              type="select"
              label={`${t('general.claimStatus')}*`}
              name="status"
              settings={{
                options:
                  config.CLAIMS.CASEFLOW_TYPE === CaseflowCaseTypes.FATBIKES
                    ? CLAIM_STATUS_OPTIONS_FATBIKES(t)
                    : CLAIM_STATUS_OPTIONS_LITE(t),
                id: 'status',
                placeholder: t('Please select an option'),
                hint: '*required',
              }}
            />
            {config.WARRANTIES.MANAGEMENT.CAN_SET_WARRANTY_STATUS && (
              <Input
                type="select"
                name="warranty_status"
                label={`${t('general.warrantyStatus')}`}
                settings={{
                  id: 'warranty_status',
                  placeholder: t('message.pleaseSelectAnOption'),
                  options: WARRANTY_STATUS_OPTIONS,
                }}
              />
            )}
            {config.WARRANTIES.MANAGEMENT.CAN_SET_PART_STATUS && (
              <Input
                type="select"
                name="part_status"
                label={`${t('general.part_status')}`}
                settings={{
                  id: 'part_status',
                  placeholder: t('message.pleaseSelectAnOption'),
                  options: PART_STATUS_OPTIONS,
                }}
              />
            )}
            <Input
              type="rich-text"
              label={t('general.commentToCustomer')}
              name="comment_to_customer"
              settings={{
                id: 'comment_to_customer',
              }}
            />
            <div className="mt-8 flex gap-8">
              <Button
                kind="primary"
                type="submit"
                loading={isSubmitting}
                disabled={errors.status !== undefined}
              >
                {t('general.updateStatus')}
              </Button>
              <Button kind="outline_red" onClick={() => onClose()}>
                {t('general.cancel')}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}
